import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  useTheme,
  alpha
} from '@mui/material';
import {
  CloudUpload,
  Close,
  InsertDriveFile,
  Delete,
  Description,
  Image,
  TableChart,
  Slideshow
} from '@mui/icons-material';

interface FileUploadItem {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface DocumentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onUploadComplete: () => void;
}

const ACCEPTED_TYPES = {
  'application/pdf': { icon: Description, label: 'PDF' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: Description, label: 'DOCX' },
  'application/vnd.ms-excel': { icon: TableChart, label: 'XLS' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: TableChart, label: 'XLSX' },
  'application/vnd.ms-powerpoint': { icon: Slideshow, label: 'PPT' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { icon: Slideshow, label: 'PPTX' },
  'text/plain': { icon: Description, label: 'TXT' },
  'text/markdown': { icon: Description, label: 'MD' },
  '.md': { icon: Description, label: 'MD' },
  '.markdown': { icon: Description, label: 'MD' },
  'text/x-markdown': { icon: Description, label: 'MD' },
  'image/png': { icon: Image, label: 'PNG' },
  'image/jpeg': { icon: Image, label: 'JPG' },
};

const FILE_EXTENSIONS = Object.keys(ACCEPTED_TYPES).flatMap(type => {
  const extension = type.split('/')[1];
  switch (type) {
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return ['.docx'];
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return ['.xlsx'];
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return ['.pptx'];
    case 'text/markdown':
      return ['.md'];
    case '.md':
      return ['.md'];
    case '.markdown':
      return ['.markdown'];
    case 'text/x-markdown':
      return ['.md'];
    default:
      return [`.${extension}`];
  }
}).join(',');

export default function DocumentUploadDialog({
  open,
  onClose,
  projectId,
  onUploadComplete
}: DocumentUploadDialogProps) {
  const theme = useTheme();
  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [globalError, setGlobalError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(file =>
      Object.keys(ACCEPTED_TYPES).includes(file.type) ||
      Object.values(ACCEPTED_TYPES).some(({ label }) =>
        file.name.toLowerCase().endsWith(label.toLowerCase())
      )
    );

    if (validFiles.length > 0) {
      addFiles(validFiles);
    } else {
      setGlobalError('不支持上传的文件格式');
    }
  }, []);

  const addFiles = (newFiles: File[]) => {
    const fileItems: FileUploadItem[] = newFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending'
    }));

    setFiles(prev => [...prev, ...fileItems]);
    setGlobalError('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      addFiles(selectedFiles);
    }
    // 清空input以允许重复选择相同文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (fileItem: FileUploadItem): Promise<void> => {
    const { file } = fileItem;

    try {
      // 使用API客户端上传文件
      const apiClient = (await import('@/services/api')).apiClient;

      await apiClient.documents.uploadToUploadDir(
        projectId,
        file,
        (progress) => {
          setFiles(prev => prev.map(item =>
            item.file === file
              ? { ...item, progress, status: 'uploading' as const }
              : item
          ));
        }
      );

      setFiles(prev => prev.map(item =>
        item.file === file
          ? { ...item, progress: 100, status: 'success' as const }
          : item
      ));
    } catch (error: any) {
      setFiles(prev => prev.map(item =>
        item.file === file
          ? { ...item, status: 'error' as const, error: error.message || '上传失败' }
          : item
      ));
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setGlobalError('');

    // 按顺序上传文件
    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i];
      if (fileItem.status === 'pending') {
        await uploadFile(fileItem);
      }
    }

    setIsUploading(false);

    // 检查是否所有文件都上传成功
    const allSuccessful = files.every(item => item.status === 'success');
    if (allSuccessful) {
      setTimeout(() => {
        onUploadComplete();
        handleClose();
        // 刷新当前页面以更新文档列表
        
      }, 1500);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      setGlobalError('');
      onClose();
    }
  };

  const getFileIcon = (file: File) => {
    const typeInfo = Object.entries(ACCEPTED_TYPES).find(([type]) =>
      file.type === type || file.name.toLowerCase().endsWith(type.split('/')[1])
    );
    const Icon = typeInfo ? typeInfo[1].icon : InsertDriveFile;
    return <Icon />;
  };

  const getFileTypeLabel = (file: File) => {
    const typeInfo = Object.entries(ACCEPTED_TYPES).find(([type]) =>
      file.type === type || file.name.toLowerCase().endsWith(type.split('/')[1])
    );
    return typeInfo ? typeInfo[1].label : 'Unknown';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const allFilesCompleted = files.length > 0 && files.every(item =>
    item.status === 'success' || item.status === 'error'
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 500
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 2
      }}>
        <Typography variant="h6" component="div">
          上传文档
        </Typography>
        <IconButton onClick={handleClose} disabled={isUploading}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {globalError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGlobalError('')}>
            {globalError}
          </Alert>
        )}

        {/* 拖拽上传区域 */}
        <Box
          sx={{
            border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            bgcolor: isDragging
              ? alpha(theme.palette.primary.main, 0.05)
              : alpha(theme.palette.action.hover, 0.02),
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            mb: 3,
            '&:hover': {
              bgcolor: alpha(theme.palette.action.hover, 0.05),
              borderColor: theme.palette.primary.main
            }
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={FILE_EXTENSIONS}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          <CloudUpload sx={{
            fontSize: 48,
            color: isDragging ? 'primary.main' : 'action.disabled',
            mb: 2
          }} />

          <Typography variant="h6" gutterBottom>
            {isDragging ? '松开以上传文件' : '拖拽文件到此处或点击选择'}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            支持格式：PDF, DOCX, XLS, XLSX, PPT, PPTX, TXT, MD, PNG, JPG
          </Typography>
        </Box>

        {/* 文件列表 */}
        {files.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              待上传文件 ({files.length})
            </Typography>

            <List dense>
              {files.map((fileItem, index) => (
                <ListItem
                  key={index}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: 'background.paper'
                  }}
                >
                  <ListItemIcon>
                    {getFileIcon(fileItem.file)}
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 200
                          }}
                        >
                          {fileItem.file.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={getFileTypeLabel(fileItem.file)}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(fileItem.file.size)}
                        </Typography>

                        {fileItem.status === 'uploading' && (
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={fileItem.progress}
                              sx={{ height: 4, borderRadius: 2 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {fileItem.progress.toFixed(0)}%
                            </Typography>
                          </Box>
                        )}

                        {fileItem.status === 'success' && (
                          <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                            ✓ 上传成功
                          </Typography>
                        )}

                        {fileItem.status === 'error' && (
                          <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                            ✗ {fileItem.error}
                          </Typography>
                        )}
                      </Box>
                    }
                  />

                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => removeFile(index)}
                      disabled={isUploading || fileItem.status === 'uploading'}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isUploading}
        >
          {allFilesCompleted ? '完成' : '取消'}
        </Button>

        {files.length > 0 && !allFilesCompleted && (
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={isUploading || files.length === 0}
            startIcon={<CloudUpload />}
          >
            {isUploading ? '上传中...' : `上传文件 (${files.filter(f => f.status === 'pending').length})`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}